/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Button, Loader, Message } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import getWallet from 'redux/actions/wallets/getWallets';
import Toolbar from 'components/common/Toolbar';
import TopUpSidebar from 'components/common/SidebarFilter/TopUpSidebar';
import CreditSidebar from 'components/common/SidebarFilter/CreditSidebar';
import { canAccess, canPerformAction } from 'helpers/permissions';
import getOrgWithWallet from 'redux/actions/organizations/getOrgWithWallet';
import refreshGroupBalances from 'redux/actions/groups/refreshGroupBalances';
import refreshUserBalances from 'redux/actions/user/refreshUserBalances';
import isParentOrganization from 'helpers/isParentOrganization';
import getUserRoles from 'helpers/getUserRoles';
import { organizationsDropDownOptions } from 'utils/dropdownOptions';
import AllTransactions from 'containers/Wallet/AllTransactions';
import {
  isFinanceRwandaAdmin,
  isOrgAdmin,
  isRwandaAdmin,
  isSSA,
} from 'helpers/checkRoles';
import WalletBalance from '../WalletBalance';
import './index.scss';

const OrganizationWallet = () => {
  const dispatch = useDispatch();
  const [actionType, setActionType] = useState();
  const [form, setForm] = useState({});
  const [refreshingGroups, setRefreshingGroups] = useState(false);
  const [refreshingUsers, setRefreshingUsers] = useState(false);
  const { push } = useHistory();
  const isCurrentOrgParent = isParentOrganization();

  const { loading, items, error } = useSelector(
    ({ wallets: { wallets } }) => wallets,
  );

  const { items: organizations = [] } = useSelector(
    ({ organizations: { hasWallet } }) => hasWallet,
  );

  const {
    currentOrgId,
    currentOrgName,
    saveType,
    orgUsers,
    roles,
  } = getUserRoles();

  const isAdmin =
    isFinanceRwandaAdmin(roles) ||
    isRwandaAdmin({ roles }) ||
    isOrgAdmin(roles) ||
    isSSA(roles);

  const [currentOrganization, setCurrentOrganization] = useState({
    name: currentOrgName,
    id: currentOrgId,
    type: saveType || 'ngos',
  });

  const { has_wallet } = useSelector(
    ({
      dashboard: {
        currentOrganization: { details = { user: {} } },
      },
    }) => ({ ...details }),
  );

  useEffect(() => {
    if (
      !isEmpty(roles) &&
      currentOrgId &&
      !canAccess('wallet', roles, {
        currentOrgId,
        orgUsers,
        additionalCondition: has_wallet,
      })
    ) {
      push('/');
    }
  }, [orgUsers, currentOrgId]);

  const handleChange = (e, { name, value }) => {
    setForm({ ...form, [name]: value });
  };

  useEffect(() => {
    if (isCurrentOrgParent) {
      getWallet()(dispatch);
      getOrgWithWallet()(dispatch);
    } else {
      getWallet(currentOrgId)(dispatch);
    }
  }, [isCurrentOrgParent]);

  const handleActionClicked = type => {
    setActionType(type);
  };

  const dropdownDefaultValue = orgs =>
    organizationsDropDownOptions(orgs).find(
      ({ value }) => value === currentOrgId,
    );

  const onDropdownChange = (e, { value }) => {
    const currentOrg = organizations?.find(org => org?.id === value);

    setCurrentOrganization({
      id: currentOrg?.id,
      name: currentOrg?.name,
      type: currentOrg?.save_type || 'ngos',
    });

    if (
      ['save_country', 'save_global'].includes(
        organizations.find(org => org.id === value).save_type,
      )
    ) {
      getWallet()(dispatch);
      return;
    }
    getWallet(value)(dispatch);
  };

  const headerContent = {
    rightComponent: (
      <>
        {isAdmin && (
          <>
            <Button
              basic
              loading={refreshingGroups}
              disabled={refreshingGroups}
              onClick={() => {
                setRefreshingGroups(true);
                refreshGroupBalances(() =>
                  setRefreshingGroups(false),
                )(dispatch);
              }}
            >
              Refresh Group wallets
            </Button>

            <Button
              basic
              loading={refreshingUsers}
              disabled={refreshingUsers}
              onClick={() => {
                setRefreshingUsers(true);
                refreshUserBalances(() => setRefreshingUsers(false))(
                  dispatch,
                );
              }}
            >
              Refresh user wallets
            </Button>
          </>
        )}

        {canPerformAction('walletTopUp', roles) && (
          <Button
            primary
            className="wallet__top-up"
            onClick={() => handleActionClicked('top-up')}
          >
            Top-up
          </Button>
        )}

        {canPerformAction('walletCredit', roles) && (
          <Button
            primary
            onClick={() => handleActionClicked('credit')}
          >
            Credit
          </Button>
        )}

        <TopUpSidebar
          {...{
            filterOn: actionType === 'top-up',
            form,
            setForm,
            handleChange,
            types: [],
            setActionType,
            roles,
            currentOrgId,
            wallets: { items },
            orgUsers,
          }}
        />

        <CreditSidebar
          {...{
            filterOn: actionType === 'credit',
            form,
            setForm,
            handleChange,
            setActionType,
            roles,
            currentOrgId,
          }}
        />
      </>
    ),
  };

  return (
    <div className="wallet">
      <Toolbar
        header={
          isCurrentOrgParent
            ? {
                walletSelection: {
                  title: 'Wallet:',
                  options: organizationsDropDownOptions(
                    organizations,
                  ),
                  defaultValue: dropdownDefaultValue(organizations),
                  onChange: onDropdownChange,
                },
                ...headerContent,
              }
            : {
                title: `Wallet: ${currentOrgName || ''}`,
                ...headerContent,
              }
        }
        body={
          <>
            {error && <Message>{error.message}</Message>}
            {loading && <Loader active inline="centered" />}
            {!loading && items && items.length > 0 && !error && (
              <WalletBalance
                data={items}
                currentOrg={currentOrganization}
              />
            )}
          </>
        }
      />
      <AllTransactions />
    </div>
  );
};

export default OrganizationWallet;
